import { RefObject, useEffect } from 'react'

interface UseBlockInteractionsOutsideOptions {
  block: boolean
  allowedRef: RefObject<HTMLElement | null>
}

const BLOCKED_EVENTS: ReadonlyArray<keyof DocumentEventMap> = ['mousedown', 'click', 'wheel', 'touchmove', 'keydown']

const isNodeInsideAllowedElement = (node: EventTarget | null, allowed: HTMLElement | null) => {
  if (!allowed || !(node instanceof Node)) {
    return false
  }

  return node === allowed || allowed.contains(node)
}

const isEventInsideAllowedElement = (event: Event, allowed: HTMLElement | null) => {
  if (isNodeInsideAllowedElement(event.target, allowed)) {
    return true
  }

  if (typeof event.composedPath === 'function') {
    return event.composedPath().some((node) => isNodeInsideAllowedElement(node, allowed))
  }

  return false
}

export function useBlockInteractionsOutside({ block, allowedRef }: UseBlockInteractionsOutsideOptions) {
  useEffect(() => {
    if (!block) {
      return
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const preventInteraction = (event: Event) => {
      if (isEventInsideAllowedElement(event, allowedRef.current)) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
    }

    BLOCKED_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, preventInteraction, { capture: true, passive: false })
    })

    return () => {
      document.body.style.overflow = previousOverflow

      BLOCKED_EVENTS.forEach((eventName) => {
        document.removeEventListener(eventName, preventInteraction, true)
      })
    }
  }, [block, allowedRef])
}
